import { deserializeObject, serializeObject } from '@app/shared';
import { deserializeListState, filterDateReviver } from './list.model';

/** The tabs of the company page, the last segment of its URL. */
export const COMPANY_TABS = [
  'overview',
  'publications',
  'people',
  'score',
] as const;
export type CompanyTab = (typeof COMPANY_TABS)[number];

export const isCompanyTab = (value: unknown): value is CompanyTab =>
  COMPANY_TABS.includes(value as CompanyTab);

/**
 * Where a company page was opened from. The state is the serialized state of
 * the list (`serializeListState`) or of the search (`serializeSearchState`),
 * as in the URL of that page, so previous/next and the way back survive a
 * reload, a new tab and a shared link.
 */
export type CompanyContext =
  | { kind: 'list'; listId: number; state: string | null }
  | { kind: 'search'; state: string | null };

/** The sorting of the search table: one column, 1 ascending, -1 descending. */
export interface SearchSort {
  field: string;
  order: 1 | -1;
}

/**
 * The state of the search page in its URL. The rows load while scrolling, so
 * there is no page; the page index of former URLs is ignored.
 */
export interface SearchState {
  filterValue: unknown[] | null;
  sort: SearchSort | null;
}

export const serializeSearchState = (state: Partial<SearchState>) =>
  serializeObject({
    filterValue: state.filterValue ?? null,
    sort: state.sort ?? null,
  });

export const deserializeSearchState = (
  input: string | null
): SearchState | null => {
  const state = deserializeObject<Partial<SearchState>>(
    input,
    filterDateReviver
  );
  if (!state || typeof state !== 'object') {
    return null;
  }
  const sort = state.sort;
  return {
    filterValue: Array.isArray(state.filterValue) ? state.filterValue : null,
    sort:
      sort && typeof sort.field === 'string'
        ? { field: sort.field, order: sort.order === -1 ? -1 : 1 }
        : null,
  };
};

/** The filter of a context, whatever kind it is. */
export const contextFilter = (context: CompanyContext): unknown[] | null =>
  (context.kind === 'list'
    ? deserializeListState(context.state)?.filterValue
    : deserializeSearchState(context.state)?.filterValue) || null;

/** The route of a company tab, in the context it was opened from. */
export const companyCommands = (
  companyId: string,
  tab: string,
  context: CompanyContext | null
): string[] =>
  context?.kind === 'list'
    ? ['/companies', 'list', String(context.listId), 'company', companyId, tab]
    : context?.kind === 'search'
      ? ['/companies', 'search', 'company', companyId, tab]
      : ['/companies', companyId, tab];

/** The query parameters that carry the state of the context. */
export const contextQueryParams = (
  context: CompanyContext | null
): { state: string } | Record<string, never> =>
  context?.state ? { state: context.state } : {};

/** The page the context stands for: the list or the search with its state. */
export const contextCommands = (context: CompanyContext): string[] =>
  context.kind === 'list'
    ? [
        '/lists',
        String(context.listId),
        ...(context.state ? [context.state] : []),
      ]
    : ['/search', ...(context.state ? [context.state] : [])];

/** One key per list and one for the search. */
export const contextKey = (context: CompanyContext) =>
  context.kind === 'list' ? `list:${context.listId}` : 'search';

/** One key per query: the list or the search with its filter (and sort). */
export const contextQueryKey = (context: CompanyContext) =>
  `${contextKey(context)}|${context.state ?? ''}`;
